export interface SubFooterLink {
  label: string;
  href: string;
}

export interface FooterColumn {
  header: string;
  position: number;
  footerType: string;
  links: Array<{
    name: string;
    url: string;
    position: number;
  }>;
}

/**
 * Filters configuration rows so client-specific overrides (e.g. GEHA)
 * replace default rows that share the same linkName, while rows without
 * an override are kept as-is.
 *
 * Lagoon columns (mutually exclusive per row):
 *  - dntClient: show this row ONLY for the specified client
 *  - dntExcludeClient: hide this row for the listed clients (comma-separated)
 */
const filterByClient = (configurations, _client: string) => {
  if (!configurations) return configurations;
  const client = _client?.toLowerCase();

  const parseClients = (value) =>
    value ? value.split(',').map((c) => c.trim().toLowerCase()) : [];

  const isExcluded = (row) =>
    parseClients(row.dntExcludeClient).includes(client || '');
  const isDefault = (row) => !row.dntClient && !isExcluded(row);
  const isClientMatch = (row) => parseClients(row.dntClient).includes(client);

  const defaults = configurations.filter(isDefault);
  const overrides = client ? configurations.filter(isClientMatch) : [];

  if (overrides.length === 0) return defaults;

  const overriddenKeys = new Set(overrides.map((row) => row.linkName));
  return [
    ...defaults.filter((row) => !overriddenKeys.has(row.linkName)),
    ...overrides,
  ];
};

export const getFooterData = (
  _configurations,
  sectionLabel = 'Section',
  generalLabel = 'General',
  isEsLocale = false,
  client = ''
): {
  section: FooterColumn[];
  general: FooterColumn[];
} => {
  const clientFiltered = filterByClient(_configurations, client);

  // Group links by column and footer type
  const columnMap = new Map<string, FooterColumn>();

  clientFiltered?.forEach((link) => {
    const key = `${link.columnPosition}-${link.footerType}`;

    if (!columnMap.has(key)) {
      columnMap.set(key, {
        header: link.columnHeaderName,
        position: Number(link.columnPosition),
        footerType: link.footerType,
        links: [],
      });
    }

    const column = columnMap.get(key)!;
    column.links.push({
      name: link.linkName,
      url: isEsLocale ? link.esLinkUrl : link.linkUrl,
      position: Number(link.linkPosition),
    });
  });

  // Sort links within each column and sort columns by position
  const allColumns = Array.from(columnMap.values())
    .map((column) => ({
      ...column,
      links: column.links.sort((a, b) => a.position - b.position),
    }))
    .sort((a, b) => a.position - b.position);

  // Match footerType against both English and translated labels to handle
  // locale-specific CMS data (e.g. Spanish "Sección" vs English "Section")
  const sectionLabels = new Set([sectionLabel, 'Section']);
  const generalLabels = new Set([generalLabel, 'General']);

  return {
    section: allColumns.filter((col) => sectionLabels.has(col.footerType)),
    general: allColumns.filter((col) => generalLabels.has(col.footerType)),
  };
};
